/**
 * TaskFlowBoardView.tsx
 *
 * High-Performance Status Column Board View for TaskFlow.
 * 100% Theme-tokenized with fluid typography, visual WIP limit warnings,
 * inline quick-add composer, keyboard-accessible card triggers, CSS logical properties,
 * and multi-locale translations via useTranslation().
 */

import React, { useState, useMemo } from 'react';
import type { TaskFlowItem, TaskFlowItemType } from '../../../plugins/taskflow/contracts/taskflow-schema.js';
import { globalTaskFlowStore } from '../../../plugins/taskflow/engine/taskflow-store.js';
import { TaskCard } from '../components/TaskCard.js';
import { useTranslation } from '../../../i18n/useTranslation.js';

export interface ColumnDefinition {
  id: string;
  titleKey: string;
  defaultTitle: string;
  wipLimit?: number;
}

export interface TaskFlowBoardViewProps {
  workspaceId: string;
  items: TaskFlowItem[];
  onSelectTask: (task: TaskFlowItem) => void;
  selectedTaskId?: string | null;
}

const DEFAULT_COLUMNS: ColumnDefinition[] = [
  { id: 'backlog', titleKey: 'taskflow.statuses.backlog', defaultTitle: '📋 Backlog' },
  { id: 'in-progress', titleKey: 'taskflow.statuses.inProgress', defaultTitle: '⚡ In Progress', wipLimit: 5 },
  { id: 'blocked', titleKey: 'taskflow.statuses.blocked', defaultTitle: '🚧 Blocked' },
  { id: 'review', titleKey: 'taskflow.statuses.review', defaultTitle: '🔍 Review', wipLimit: 4 },
  { id: 'done', titleKey: 'taskflow.statuses.done', defaultTitle: '✅ Done' },
];

export const TaskFlowBoardView: React.FC<TaskFlowBoardViewProps> = ({
  workspaceId,
  items,
  onSelectTask,
  selectedTaskId,
}) => {
  const { t } = useTranslation();
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [quickAddColumn, setQuickAddColumn] = useState<string | null>(null);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddType, setQuickAddType] = useState<TaskFlowItemType>('task');

  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.dataTransfer.setData('text/plain', id);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedItemId(id);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDropOnColumn = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    const id = e.dataTransfer.getData('text/plain') || draggedItemId;
    if (!id) return;

    const existing = globalTaskFlowStore.getItem(id);
    if (existing && existing.status !== columnId) {
      globalTaskFlowStore.updateItem(id, {
        patch: { status: columnId },
        actorId: 'user',
      });
    }
    setDraggedItemId(null);
  };

  const handleQuickAdd = (columnId: string) => {
    if (!quickAddTitle.trim()) return;

    globalTaskFlowStore.createItem({
      workspaceId,
      type: quickAddType,
      title: quickAddTitle.trim(),
      status: columnId,
    }, 'user');

    setQuickAddTitle('');
    setQuickAddColumn(null);
  };

  return (
    <div
      role="region"
      aria-label={t('taskflow.views.board') || 'TaskFlow Kanban Board'}
      style={{
        display: 'flex',
        gap: '16px',
        height: '100%',
        width: '100%',
        overflowX: 'auto',
        padding: '16px',
        boxSizing: 'border-box',
        backgroundColor: 'var(--theme-bg-main)',
      }}
    >
      {DEFAULT_COLUMNS.map((col) => {
        const columnItems = items.filter((i) => i.status === col.id);
        const isOverLimit = col.wipLimit && columnItems.length > col.wipLimit;
        const isDropping = dragOverColumn === col.id;
        const localizedTitle = t(col.titleKey) ? `${col.defaultTitle.split(' ')[0]} ${t(col.titleKey)}` : col.defaultTitle;

        return (
          <div
            key={col.id}
            role="region"
            aria-label={`${localizedTitle} column`}
            onDragOver={(e) => handleDragOver(e, col.id)}
            onDragLeave={() => setDragOverColumn(null)}
            onDrop={(e) => handleDropOnColumn(e, col.id)}
            style={{
              flex: '0 0 300px',
              backgroundColor: isDropping
                ? 'var(--theme-bg-card-hover)'
                : 'var(--theme-bg-card)',
              border: isDropping
                ? '1px dashed var(--theme-accent)'
                : '1px solid var(--theme-border-subtle)',
              borderRadius: 'var(--theme-radius-lg, 12px)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '100%',
              boxShadow: 'var(--theme-shadow-sm)',
              transition: 'background-color var(--theme-transition-fast), border-color var(--theme-transition-fast)',
            }}
          >
            {/* Column Header */}
            <div
              style={{
                padding: '12px 14px',
                borderBottom: '1px solid var(--theme-border-subtle)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: 'var(--theme-text-sm)', fontWeight: 700, color: 'var(--theme-text-primary)' }}>
                  {localizedTitle}
                </span>
                <span
                  style={{
                    fontSize: 'var(--theme-text-xs)',
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: 'var(--theme-radius-full, 9999px)',
                    backgroundColor: isOverLimit
                      ? 'var(--theme-status-danger-bg)'
                      : 'var(--theme-bg-subtle)',
                    color: isOverLimit
                      ? 'var(--theme-status-danger-text)'
                      : 'var(--theme-text-muted)',
                  }}
                >
                  {columnItems.length} {col.wipLimit ? `/ ${col.wipLimit}` : ''}
                </span>
              </div>

              <button
                onClick={() => setQuickAddColumn(quickAddColumn === col.id ? null : col.id)}
                title={`Quick add task to ${localizedTitle}`}
                aria-label={`Quick add task to ${localizedTitle}`}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--theme-text-muted)',
                  cursor: 'pointer',
                  fontSize: 'var(--theme-text-lg)',
                  lineHeight: '1',
                  padding: '2px 6px',
                  borderRadius: 'var(--theme-radius-sm, 4px)',
                }}
              >
                +
              </button>
            </div>

            {/* Quick Add Inline Composer */}
            {quickAddColumn === col.id && (
              <div
                style={{
                  padding: '10px',
                  backgroundColor: 'var(--theme-bg-card)',
                  borderBottom: '1px solid var(--theme-border-subtle)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                <div style={{ display: 'flex', gap: '6px' }}>
                  <select
                    aria-label="Task type"
                    value={quickAddType}
                    onChange={(e) => setQuickAddType(e.target.value as TaskFlowItemType)}
                    style={{
                      backgroundColor: 'var(--theme-bg-input)',
                      color: 'var(--theme-accent)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 'var(--theme-radius-sm, 4px)',
                      fontSize: 'var(--theme-text-xs)',
                      padding: '2px 4px',
                      outline: 'none',
                    }}
                  >
                    <option value="task">✅ {t('taskflow.types.task') || 'Task'}</option>
                    <option value="action">⚡ {t('taskflow.types.action') || 'Action'}</option>
                    <option value="project">📦 {t('taskflow.types.project') || 'Project'}</option>
                    <option value="goal">🎯 {t('taskflow.types.goal') || 'Goal'}</option>
                    <option value="plan">🗺️ {t('taskflow.types.plan') || 'Plan'}</option>
                  </select>

                  <input
                    type="text"
                    autoFocus
                    aria-label="New task title"
                    placeholder="Task title... (Enter to save)"
                    value={quickAddTitle}
                    onChange={(e) => setQuickAddTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleQuickAdd(col.id);
                      } else if (e.key === 'Escape') {
                        setQuickAddColumn(null);
                      }
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: 'var(--theme-bg-input)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 'var(--theme-radius-sm, 4px)',
                      color: 'var(--theme-text-primary)',
                      fontSize: 'var(--theme-text-xs)',
                      padding: '4px 8px',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                  <button
                    onClick={() => setQuickAddColumn(null)}
                    style={{
                      padding: '2px 8px',
                      fontSize: 'var(--theme-text-xs)',
                      background: 'none',
                      border: 'none',
                      color: 'var(--theme-text-muted)',
                      cursor: 'pointer',
                    }}
                  >
                    {t('common.cancel') || 'Cancel'}
                  </button>
                  <button
                    onClick={() => handleQuickAdd(col.id)}
                    style={{
                      padding: '3px 10px',
                      fontSize: 'var(--theme-text-xs)',
                      backgroundColor: 'var(--theme-accent)',
                      color: 'var(--theme-text-inverse, #fff)',
                      border: 'none',
                      borderRadius: 'var(--theme-radius-sm, 4px)',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {t('common.save') || 'Add'}
                  </button>
                </div>
              </div>
            )}

            {/* Column Cards List */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '10px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              {columnItems.length === 0 ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '20px 0',
                    fontSize: 'var(--theme-text-xs)',
                    color: 'var(--theme-text-muted)',
                  }}
                >
                  No items in {localizedTitle}
                </div>
              ) : (
                columnItems.map((item) => (
                  <TaskCard
                    key={item.id}
                    item={item}
                    isSelected={selectedTaskId === item.id}
                    onClick={() => onSelectTask(item)}
                    onDragStart={(e) => handleDragStart(e, item.id)}
                    onDragEnd={() => {
                      setDraggedItemId(null);
                      setDragOverColumn(null);
                    }}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
